import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useGetProjectById } from '../../hooks';
import {
  isOrgAdmin,
  isOrgSuperAdmin,
  useGetRoles,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import { type ProjectRole } from '../../types';
import FormSelect from '../form/FormSelect';
import SearchInput from '../SearchInput';

import InviteProjectFormDialog from './InviteProjectFormDialog';
import InviteShareProjectFormDialog from './InviteShareProjectFormDialog';
import ProjectMembersTable from './ProjectMembersTable';

const ProjectMembersManagement = () => {
  const [openInviteFormDialog, setOpenInviteFormDialog] = useState(false);

  const { projectRole, orgRole } = useGetRoles();

  const { projectId, orgId } = useGetRouteParams();

  const form = useForm();

  const { watch } = form;

  const filterProjectRole = watch('filter.projectRole');

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const searchKey = watch('search');

  const isAdmin =
    projectRole === 'project_owner' ||
    (orgRole && (isOrgAdmin(orgRole) || isOrgSuperAdmin(orgRole)));

  const handleOpenInviteFormDialog = () => {
    setOpenInviteFormDialog(true);
  };

  const handleCloseInviteFormDialog = () => {
    setOpenInviteFormDialog(false);
  };

  return (
    <div className="mx-auto flex flex-col gap-6 px-12 py-6">
      <div className="flex justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-[#333]">Members</h1>
        </div>
        <div>
          {project?.is_guest_project && <InviteShareProjectFormDialog />}
          {isAdmin && !project?.is_guest_project && project && (
            <InviteProjectFormDialog
              project={project}
              inviteUserMode="multiple"
              projectMembers={project.members}
              open={openInviteFormDialog}
              handleClose={handleCloseInviteFormDialog}
              handleOpen={handleOpenInviteFormDialog}
            />
          )}
        </div>
      </div>
      <FormProvider {...form}>
        <form className="flex w-full gap-2">
          <div className="max-w-[300px] w-full">
            <SearchInput />
          </div>
          <div className="w-[110px]">
            <FormSelect
              name={'filter.projectRole'}
              defaultValue="roles"
              fullWidth
              options={[
                {
                  value: 'roles',
                  label: 'Roles',
                },
                {
                  value: 'project_owner',
                  label: 'Owner',
                },
                {
                  value: 'project_member',
                  label: 'Member',
                },
              ]}
            />
          </div>
        </form>
      </FormProvider>
      <div>
        <ProjectMembersTable
          searchKey={searchKey}
          filters={{
            role:
              filterProjectRole !== 'roles'
                ? (filterProjectRole as ProjectRole)
                : undefined,
          }}
        />
      </div>
    </div>
  );
};

export default ProjectMembersManagement;
